import { defineStore } from "pinia";
import { useAatStore } from "@/stores/aat.js";
import { getLinksFromIdManager, getElasticData } from "./localActionSupport";
import * as colorHelpers from "@/helpers/colorHelpers";
import * as configHelper from "@/helpers/configHelper";
import * as localHumanMadeObjectHelpers from "@/helpers/HumanMadeObject";
import {
  getAllStubs,
  getExhibitionArray,
  getExhibitionAndLocations
} from "@/helpers/Exhibition";
import {
  containsCustomManifest,
  getManifestThumbnail,
  getTechnicalMetadataFields,
  removeEmpty
} from "@/helpers/LinkedArt";
import {
  getHumanMadeObjectMetadata,
  setMetaValues
} from "@/helpers/MetaTagHelper";

import {
  getOwners,
  getProvenanceData,
  getOverallProvenanceNotes
} from "@/helpers/ProvenanceHelpers";
import { transformSearchResultToThumbnailItemWithArtist } from "@/helpers/SearchItemHelper";
import { getVisualItemMetadata } from "@/helpers/VisualItem";

import { dataService, getIndexedId } from "@thegetty-private/rad-app-utils";
import {
  getName,
  getObjectNumber,
  getObjectDescription,
  convertToTitleCase,
  getLocationMessage,
  getCopyrightStatement,
  getManifestUrls,
  IIIF_V2,
  IIIF_V3,
  getManuscriptNumber
} from "@thegetty-private/linked-data.js";
import { getClassifiedAs, normalizeFieldToArray } from "@thegetty/linkedart.js";

import * as aat from "@/data/aat";
import * as iiif from "@/data/iiif";
import internal from "@/data/internal-ontologies";

let apiUrl = configHelper.getApiPath("/");
let dataUrl = configHelper.getDataUrl("/");
let mediaBaseUrl = configHelper.getMediaGatewayUrl("/");
let idManagerDataUrl = configHelper.getIdManagerDataUrl(dataUrl);
let IGNORE_MEDIA_HOST = configHelper.ignoreMediaHost();
/*
 * allow the overwriting of the app url (testing)
 *
 * @param {string} url
 */
export function setApiPath(url) {
  apiUrl = url;
}

/**
 * allow the overwriting of the data url (testing)
 *
 * @param {string} url
 */
export function setDataUrl(url) {
  dataUrl = url;
}

/**
 * Sets the ID manger Data Url
 *
 * @param {string} url
 */
export function setIdManagerDataUrl(url) {
  idManagerDataUrl = url;
}

/**
 * allow the overwriting of the data url (testing)
 *
 * @param {string} url
 */
export function setMediaDataUrl(url) {
  mediaBaseUrl = url;
}

export const useObjectStore = defineStore("object", {
  state: () => ({
    items: {}
  }),
  actions: {
    /**
     * Adds an object to the store
     *
     * @param {string} indexedId  - the indexed id
     * @param {function} translate  - the vueI18n translate function
     * @param {function} localizeNumber  - the vueI18n localization number function
     * @param {string} preferredLang  - the preferred language
     * @async
     */
    async addObject(
      indexedId,
      translate,
      localizeNumber,
      preferredLang = "en"
    ) {
      translate = translate ?? this.i18n.t;
      localizeNumber = localizeNumber ?? this.i18n.n;
      // does hmo exist in the store already?
      if (this.items[indexedId]) {
        return;
      }

      const aatStore = useAatStore();

      let lodObject = undefined;
      try {
        this.items[indexedId] = { id: indexedId };
        lodObject = await dataService.getAllById(indexedId, dataUrl);
      } catch (err) {
        console.error(err);

        // remove from store if we weren't able to get response from LOD
        delete this.items[indexedId];
        return;
      }

      if (lodObject == undefined) {
        delete this.items[indexedId];
        return;
      }

      const ref = this.items[indexedId];

      let artists = localHumanMadeObjectHelpers.getMetadataProducers(lodObject);
      artists.forEach((artist, index) => {
        // if artist is a string, and ends in "unknown", then add suffix if suffix doesn't exist
        if (
          artist.id == undefined &&
          artist.name == "Unknown" &&
          (artist.afterLink == undefined || artist.afterLink == "")
        ) {
          artist.name = translate("artist.unknown");
        }
        if (typeof artist !== "string") {
          // we need unique ids on the page, this removes the conflict with the homepage
          artist.key = `${artist.id}_${index}_title_and_artist`;
        }
      });

      ref.objectProfile = {
        title: getName(lodObject),
        artists: artists,
        copyrightStatement: getCopyrightStatement(lodObject, aat.COPYRIGHT),
        objectNumber: getObjectNumber(lodObject),
        captionDetails: localHumanMadeObjectHelpers.getCaptionDetails(
          getIndexedId(lodObject.id),
          lodObject
        ),
        citation: localHumanMadeObjectHelpers.getObjectCitation(
          lodObject,
          translate
        ),
        description: getObjectDescription(lodObject, "text/markdown"),
        creationDate: localHumanMadeObjectHelpers.getDateCreated(lodObject)
      };
      // get elasticsearch data
      let elasticData = await getElasticData(indexedId, apiUrl);

      try {
        if (elasticData) {
          let metaValues = getHumanMadeObjectMetadata(elasticData, translate);
          ref.head = {
            title: elasticData?.primary_name,
            metadata: setMetaValues(metaValues)
          };
        }
      } catch (e) {
        console.error(e);
      }

      ref.isOpenContent = elasticData?.open_content;

      ref.isDeaccessioned =
        localHumanMadeObjectHelpers.isDeaccessioned(lodObject);

      await addObjectProfileInformation(
        lodObject,
        elasticData,
        ref,
        translate,
        artists
      );

      // apply slugs/routes to just top level links showing in objectProfile
      let objectProfileLookups = [
        ...(ref.objectProfile?.artists || []),
        ...ref.objectProfile?.currentLocations
      ];

      // map of slugs that have been requested
      let slugs = {};

      await addRoutesToObjects(objectProfileLookups, slugs);

      // get complex set / book information

      let parts = await addStructuralData(
        indexedId,
        lodObject,
        elasticData,
        ref,
        translate
      );

      await addMetadata(
        lodObject,
        ref,
        parts,
        aatStore,
        translate,
        localizeNumber
      );

      // get related media
      await addRelatedMedia(lodObject, ref, preferredLang);

      // get provenance
      await addProvenance(lodObject, ref);

      // get bibliography
      await addBibliography(lodObject, ref);

      // get exhibitions
      await addExhibitionInformation(lodObject, ref);

      let hasTabData = false;
      if (
        ref.provenance?.data?.length > 0 ||
        ref.exhibitions?.length > 0 ||
        ref.bibliography?.length > 0
      ) {
        hasTabData = true;
      }
      ref.hasTabData = hasTabData;

      let lookupObjects = [
        ...(ref.metadata?.artist || []),
        ...(ref.metadata?.previous_attribution || []),
        ...(ref.exhibitions || [])
      ];

      (ref.provenance.data || []).forEach((entry) => {
        let owners = entry?.owners || [];
        owners.forEach((o) => {
          lookupObjects.push(o);
        });
      });

      await addRoutesToObjects(lookupObjects, slugs);

      // technical metadata
      let technicalMetadata = getTechnicalMetadataFields(
        lodObject,
        translate,
        ref.isOpenContent
      );

      const manifests = technicalMetadata?.iiif?.manifests || [];
      // if there's more than one manifest without a title, then add explicit version labels
      if (manifests.find((m) => m.title == undefined)?.length > 1) {
        manifests.forEach((m) => {
          if (m.format == IIIF_V3) {
            m.title = translate("m-technical-data.manifest_url_v3");
          }
          if (m.format == IIIF_V2) {
            m.title = translate("m-technical-data.manifest_url_v2");
          }
        });
      }
      // check for case where we don't actually get any manifest data back - DEV-18324
      // NOTE: check is different from RCV, reminder to check this if we make any imageViewer / related changes

      if (!ref.manifestLoaded) {
        delete technicalMetadata.iiif;
      }

      ref.technicalMetadata = technicalMetadata;
    },
    /**
     * Adds an object to the store with form information
     *
     * @param {string} indexedId  - the indexed id
     * @param {function} translate  - the vueI18n translate function
     * @param {function} localizeNumber  - the vueI18n localization number function
     * @param {string} imageServiceUrl - the Image Service URL for the requested Image
     * @param {string} preferredLang  - the preferred language
     * @async
     */

    async addObjectWithRequestFormData(
      indexedId,
      translate,
      localizeNumber,
      imageServiceUrl,
      preferredLang = "en"
    ) {
      translate = translate ?? this.i18n.t;
      localizeNumber = localizeNumber ?? this.i18n.n;

      if (this.items[indexedId] == undefined) {
        await this.addObject(
          indexedId,
          translate,
          localizeNumber,
          preferredLang
        );
      }
      let ref = this.items[indexedId];

      if (ref.requestForm == undefined) {
        ref.requestForm = {};
      }

      if (imageServiceUrl == undefined) {
        imageServiceUrl = ref.objectProfile?.mediaItems[0]?.iiifImageId;
      }

      // already requested
      const itemRef = ref.requestForm[imageServiceUrl];
      if (
        itemRef != undefined &&
        Object.values(itemRef).filter((a) => a).length > 0
      ) {
        return;
      }
      try {
        let results = await getLinksFromIdManager([
          {
            motivation: internal.ID_MANAGER_IMAGE_OF_MOTIVATION,
            body_generator: internal.ID_MANAGER_GCIS_GENERATOR,
            target_generator: internal.ID_MANAGER_OTMM_GENERATOR,
            body_id: imageServiceUrl
          }
        ]);
        results = results[internal.ID_MANAGER_OTMM_GENERATOR];
        const first = results[0]?.id;
        const otmm_id = first.substring(first.lastIndexOf(":") + 1);

        // get the artist names
        const artists = JSON.parse(JSON.stringify(ref.metadata.artist));
        const names = [];
        artists.forEach((artist) => {
          if (artist.name) {
            names.push(artist.name);
            delete artist.afterLink;
            delete artist.to;
          } else if (typeof artist === "string") {
            names.push(artist);
          } else {
            names.push(translate("artist.unknown_short"));
          }
        });

        // construct the return object
        const captionDetails = JSON.parse(
          JSON.stringify(ref.objectProfile.captionDetails)
        );
        captionDetails.artists = artists;

        ref.requestForm[imageServiceUrl] = {
          tmsAccessionNumber: captionDetails.object_number,
          title: captionDetails.title,
          id: otmm_id,
          author:
            artists.length > 0
              ? names.join(", ")
              : translate("artist.unknown_short")
        };
      } catch (e) {
        console.error(e);
      }
    }
  },
  getters: {
    // NOTE: getters do not accept parameters, but can return a function that receives one
    /**
     * Gets the transformed object data object from the store
     * @param {object} state - automatic access to state
     * @return {object}
     * @example
     *  {
     *    description: "",
     *    id: "type/1234",
     *    isGetty: false,
     *    metadata: {...},
     *    pageTitle: "Vincent van Gogh",
     *    relatedResourcesLinks: [],
     *    subheading: "Dutch, 1853 - 1890",
     *    technicalMetadata: {...},
     *    type: "Person"
     *  }
     */
    getObject(state) {
      /**
       * [returned function of getter - returns a function that has access to state & can receive a paramater]
       * @param {string} indexedId - indexedId of actor to retrieve from store
       */
      return (indexedId) => {
        if (state.items[indexedId]) {
          return state.items[indexedId];
        } else {
          return undefined;
        }
      };
    }
  }
});

/**
 * Adds the structural data to the store
 *
 * @param {string} indexedId - the indexed id
 * @param {object} lodObject - the HMO LOD object
 * @param {object} elasticData - the elastic search data
 * @param {object} ref - the reference to the store
 * @param {function} translate - the translate function
 * @async
 *
 * @returns {array} a list of parts
 */
async function addStructuralData(
  indexedId,
  lodObject,
  elasticData = {},
  ref,
  translate
) {
  let hasParts = localHumanMadeObjectHelpers.getNumberOfParts(lodObject) > 0;
  ref.structure = {
    localeKey:
      localHumanMadeObjectHelpers.getRecordStructureLocaleKey(lodObject),
    isParent: elasticData?.is_parent,
    isPart: elasticData?.is_part,
    isRoot: elasticData?.is_root,
    rootId: elasticData?.root_id,
    hasParts: hasParts,
    isStandalone: elasticData?.is_standalone,
    parentId: elasticData?.parent_id,
    parentTree: elasticData?.parent_tree,
    level: elasticData?.level,
    numberOfChildren: elasticData?.number_of_children,
    complexObjectType:
      localHumanMadeObjectHelpers.getComplexObjectType(lodObject)
  };

  let complexObjectResponse = [];
  try {
    let resp = await dataService.axiosGet(
      apiUrl + "api/object-tree",
      undefined,
      { id: indexedId }
    );
    complexObjectResponse = resp?.data ?? [];
  } catch (e) {
    console.error(e);
  }

  let parts = complexObjectResponse?.parts?.hits || [];
  let partsHaveDistinctImages = false;
  let partImage = undefined;
  for (let part of parts) {
    if (part["manifest"]) {
      if (
        partImage != undefined &&
        partImage != part["manifest"]["thumbUuid"]
      ) {
        partsHaveDistinctImages = true;
        break;
      }
      partImage = part["manifest"]["thumbUuid"];
    }
  }

  ref.structure.partsHaveDistinctImages = partsHaveDistinctImages;
  ref.structure.isManuscript =
    localHumanMadeObjectHelpers.showBookViewer(lodObject);

  ref.structure.manuscript_number = getManuscriptNumber(
    lodObject,
    aat.MANUSCRIPT_NUMBER
  );
  let showParts = false;
  let showManuscriptNumber = false;
  if (
    ref.structure.isManuscript ||
    ref.structure.complexObjectType == "manuscript" ||
    ref.structure.manuscript_number != undefined
  ) {
    showParts = false;
    showManuscriptNumber = true;
  }

  ref.structure.parts = parts.map((item) =>
    transformSearchResultToThumbnailItemWithArtist(
      item,
      translate,
      showParts,
      showManuscriptNumber
    )
  );
  let partOf = complexObjectResponse?.part_of?.hits || [];
  if (partOf.length) {
    ref.structure.partOf = transformSearchResultToThumbnailItemWithArtist(
      partOf[0],
      translate,
      showParts,
      showManuscriptNumber
    );
  }

  let allObjects = complexObjectResponse?.all_objects?.hits || [];
  ref.structure.allObjects = allObjects.map((item) =>
    transformSearchResultToThumbnailItemWithArtist(
      item,
      translate,
      showParts,
      showManuscriptNumber
    )
  );
  ref.structure.hasAllObjects = allObjects.length > 0;
  let displayManuscriptPartsComponent = true;
  ref.structure.isComplexObject =
    ref.structure.parts.length > 0 ||
    ref.structure.allObjects.length > 0 ||
    ref.structure.isRoot;

  if (
    elasticData?.is_standalone !== true ||
    hasParts > 0 ||
    elasticData?.number_of_children > 0
  ) {
    let title = getName(lodObject);

    let heading = translate("m-parts-of-set.heading", {
      title: title
    });

    if (ref.structure.isManuscript == true) {
      heading = translate("m-parts-of-set.manuscript_heading", {
        title: title
      });
    } else if (ref.structure?.hasAllObjects && elasticData?.is_root == false) {
      heading = localHumanMadeObjectHelpers.getPartsCarouselHeading(
        lodObject,
        translate
      );
    }
    ref.structure.partsCarouselHeading = heading;
  }

  if (
    (ref.structure.complexObjectType == "fragment" ||
      (ref.structure.complexObjectType == "set" &&
        partsHaveDistinctImages == false) ||
      (ref.structure.complexObjectType == "manuscript" &&
        displayManuscriptPartsComponent == false)) &&
    ref.structure.parts.length
  ) {
    return parts;
  }

  // set whether or not to display the parts carousel
  ref.structure.showPartsCarousel =
    localHumanMadeObjectHelpers.shouldDisplayPartsCarousel(
      ref.structure.hasParts,
      ref.structure?.hasAllObjects,
      ref.structure.complexObjectType,
      ref.structure.partsHaveDistinctImages,
      ref.structure.isRoot
    );

  return [];
}

/**
 * Adds more of the object profile information to the store
 *
 * @param {object} lodObject - the HMO LOD object
 * @param {object} elasticData - the elasticsearch data
 * @param {object} ref - the store reference
 * @param {function} translate - the translate function
 * @param {array} artists - the list of artists
 * @async
 */
async function addObjectProfileInformation(
  lodObject,
  elasticData,
  ref,
  translate,
  artists
) {
  ref.hasCanvases = false;

  let manifestFormat = configHelper.getIIIFVersion() === 3 ? IIIF_V3 : IIIF_V2;

  let manifestUrls = getManifestUrls(lodObject, {
    format: manifestFormat,
    excludeCustom: !configHelper.useCustomManifest
  });

  ref.manifestUrl = manifestUrls[0];
  ref.containsCustomManifest = containsCustomManifest(lodObject);
  ref.sequenceLabelSource =
    localHumanMadeObjectHelpers.getSequenceLabelSource(lodObject);
  let manifest = undefined;

  if (ref.manifestUrl != undefined) {
    try {
      let resp = await dataService.axiosGet(ref.manifestUrl);
      if (resp != undefined) {
        manifest = resp?.data;
        ref.manifestLoaded = true;
      }
    } catch (e) {
      ref.manifestLoaded = false;
      console.error(e);
    }
  }

  let visualItemStubs = localHumanMadeObjectHelpers
    .getVisualItemStubs(lodObject)
    .map((o) => o.id);
  let visualItems = [];
  try {
    visualItems = await dataService.getAllById(visualItemStubs, mediaBaseUrl);
  } catch (e) {
    console.error(e);
  }

  let currentLocations = [];
  try {
    currentLocations = await dataService.getAllById(
      localHumanMadeObjectHelpers
        .getCurrentLocations(lodObject)
        .map((o) => o.id),
      dataUrl
    );

    currentLocations = currentLocations
      .filter((place) => {
        // if the place object isn't classified as on display/a gallery, don't get the location message
        return (
          place != undefined && getClassifiedAs(place, aat.ON_DISPLAY).length
        );
      })
      .map((place) => {
        let indexedId = getIndexedId(place.id);
        return {
          id: indexedId,
          name: getLocationMessage(place)
        };
      });
  } catch (e) {
    console.error(e);
  }

  let mediaItems = buildMediaItems(manifest, visualItems, ref);

  (ref.isManuscriptWithParts =
    localHumanMadeObjectHelpers.showBookViewer(lodObject) &&
    localHumanMadeObjectHelpers.getNumberOfParts(lodObject) > 0),
    Object.assign(ref.objectProfile, {
      numberOfChildren: elasticData?.number_of_children || 0,
      currentLocations: currentLocations,
      mediaItems: mediaItems
    });

  if (elasticData?.colors?.length > 0 && ref.isDeaccessioned === false) {
    // if custom background is found, set variable so we don't get stuck in loop here
    let customColor = colorHelpers.getClosestDarkColor(elasticData.colors);
    ref.objectProfile.backgroundColor = customColor;
  } else {
    ref.objectProfile.backgroundColor = colorHelpers.getDefaultColor();
  }

  let stickyBoxArtists = "";
  if (artists && artists.length > 0 && artists[0] != undefined) {
    // handle unknown artists
    stickyBoxArtists = artists[0].name ? artists[0].name : artists[0];
    // handle multiple artists
    if (artists.length > 1) {
      stickyBoxArtists = translate("stickybox.artists", {
        name: artists[0].name,
        number: artists.length - 1
      });
    }
  }

  let deaccessionStatement =
    ref.isDeaccessioned &&
    localHumanMadeObjectHelpers.getDeaccessionStatement(lodObject);
  if (deaccessionStatement) {
    ref.objectProfile.deaccessionStatement = deaccessionStatement;
  }

  let deaccessionStatementLong =
    ref.isDeaccessioned &&
    localHumanMadeObjectHelpers.getDeaccessionStatementLong(lodObject);
  if (deaccessionStatementLong) {
    ref.objectProfile.deaccessionStatementLong = deaccessionStatementLong;
  }

  let firstMediaItem = {};
  if (ref.objectProfile?.mediaItems?.length) {
    firstMediaItem = ref.objectProfile?.mediaItems[0];
  }

  let stickyBoxData = {
    heading: ref.objectProfile.title,
    subheading: stickyBoxArtists,
    imageUuid: firstMediaItem?.iiifImageId?.replace(
      "https://media.getty.edu/iiif/image/",
      ""
    ),
    imageAlt: firstMediaItem?.altText
  };

  // if there's a manuscript
  if (ref.objectProfile.mediaItems?.length == 0 && manifest) {
    stickyBoxData.imageUuid = getManifestThumbnail(manifest);
  }

  if (ref.isDeaccessioned) {
    stickyBoxData.isDeaccessioned = true;
  }

  ref.stickyBoxData = stickyBoxData;
}

/**
 *
 * @param {object} manifest - the IIIF Manifest
 * @param {array} visualItems - the list of VisualItems
 * @param {object} ref - the object to store
 *
 * @returns {array}
 */
function buildMediaItems(manifest, visualItems, ref) {
  if (!manifest) {
    return;
  }
  const canvases =
    manifest["@context"] == iiif.PRESENTATION_V2
      ? manifest?.sequences[0]?.canvases
      : manifest?.items;

  if (canvases?.length > 0) {
    ref.hasCanvases = true;
  }

  let mediaItems = getVisualItemMetadata(visualItems);

  mediaItems = mediaItems.reduce((acc, item) => {
    // @todo for now only return canvases for which we haeve a corresponding
    // visual item, may want to revisit in the future
    let canvas;
    if (manifest["@context"] == iiif.PRESENTATION_V2) {
      // special handling to exclude host (when in staging) because of known discrepencies
      if (IGNORE_MEDIA_HOST) {
        canvas = canvases.find(
          (canvas) =>
            canvas.images[0].resource.service["@id"]
              .split("/")
              .slice(3)
              .join("/") == item.iiifImageId.split("/").slice(3).join("/")
        );
      } else {
        canvas = canvases.find(
          (canvas) =>
            canvas.images[0].resource.service["@id"] == item.iiifImageId
        );
      }
    } else {
      canvas = canvases.find(
        (canvas) =>
          canvas.items[0].items[0].body.type == "Image" &&
          canvas.items[0].items[0].body.service[0]["id"] == item.iiifImageId
      );
    }

    if (canvas) {
      item = {
        ...item,
        canvasId: canvas["id"] || canvas["@id"]
      };
      acc = acc.concat(item);
    }

    return acc;
  }, []);

  return mediaItems;
}
/**
 * Adds the metadata to the Store
 *
 * @param {object} lodObject - the HMO
 * @param {object} ref - the ref to the store
 * @param {array} parts - the list of parts of the object
 * @param {object} aatStore - the aat store
 * @param {function} translate - the translate function
 * @param {function} localizeNumber - the number localization function
 * @async
 */
async function addMetadata(
  lodObject,
  ref,
  parts,
  aatStore,
  translate,
  localizeNumber
) {
  let currentKeepers = [];
  try {
    currentKeepers = await dataService.getAllById(
      localHumanMadeObjectHelpers.getCurrentKeepers(lodObject).map((a) => a.id),
      dataUrl
    );
  } catch (e) {
    console.error(e);
  }

  const departments = currentKeepers.map((keeper) => getName(keeper));
  let producers = localHumanMadeObjectHelpers.getMetadataProducers(lodObject);
  let prevProducers =
    localHumanMadeObjectHelpers.getPreviouslyAttributedProducers(lodObject);
  let actors = [];
  try {
    let actorIds = [...producers, ...prevProducers]
      // filter to known producers and then grab just the ids
      .filter((obj) => {
        return obj && localHumanMadeObjectHelpers.isMakerKnown(obj);
      })
      .map((a) => a.id)
      .filter((o) => o != undefined);
    actors = await dataService.getAllById(actorIds, dataUrl);
  } catch (e) {
    console.error(e);
  }
  let map = {};
  actors.forEach((a) => {
    if (a) {
      map[getIndexedId(a.id)] = a;
    }
  });

  prevProducers = localHumanMadeObjectHelpers.getPreviousProducerAttributions(
    lodObject,
    actors
  );

  let classifications = [];

  for (let term of getClassifiedAs(
    normalizeFieldToArray(lodObject, "classified_as"),
    aat.CLASSIFICATION
  )) {
    if (term && term.id.includes("aat") == false) {
      continue;
    }
    await aatStore.addAat(term.id);
    classifications.push(aatStore.getAat(term.id)?.value);
  }

  classifications = classifications.map((classification) =>
    convertToTitleCase(classification)
  );

  const fragmentPartLinks = localHumanMadeObjectHelpers.linkifyFragments(
    lodObject,
    parts,
    getIndexedId(lodObject.id),
    translate,
    localizeNumber
  );

  ref.relatedLinks = localHumanMadeObjectHelpers.getRelatedLinks(lodObject);

  ref.metadata = localHumanMadeObjectHelpers.getMetadataFields(
    lodObject,
    classifications,
    departments,
    producers,
    prevProducers,
    fragmentPartLinks,
    translate
  );
}

/**
 * Adds the related media to the store
 *
 * @param {object} lodObject - the HMO
 * @param {object} ref - the ref to the store
 * @param {string} preferredLang - the preferred language
 * @async
 */
async function addRelatedMedia(lodObject, ref, preferredLang) {
  let typeMap = undefined;
  const mediaInfo = {};
  try {
    typeMap = await getLinksFromIdManager([
      {
        motivation: internal.ID_MANAGER_RELATED_CONTENT_MOTIVATION,
        body_generator: internal.ID_MANAGER_TARGET_GENERATOR,
        body_id: lodObject.id
      },
      {
        motivation: internal.ID_MANAGER_RELATED_CONTENT_MOTIVATION,
        body_generator: internal.ID_MANAGER_RELATED_COLLECTION_LOD_GENERATOR,
        body_id: lodObject.id
      }
    ]);
    if (typeMap[internal.ID_MANAGER_TARGET_GENERATOR]) {
      let audio = await dataService.getAllById(
        typeMap[internal.ID_MANAGER_TARGET_GENERATOR].map((a) => a.id),
        mediaBaseUrl
      );
      // return array of arrays of linguistic object formatted details
      let relatedAudioDetails = [];
      for (let item of localHumanMadeObjectHelpers.getRelatedMediaDetails(
        audio
      )) {
        if (!item.length) {
          continue;
        }
        relatedAudioDetails.push({
          sources: item.map((e) => {
            if (e.language == preferredLang) {
              e.preferred = true;
            }
            return { ...e, downloadURL: e.url, src: e.url };
          })
        });
      }
      if (relatedAudioDetails.length > 0) {
        mediaInfo.audio = relatedAudioDetails;
      }
    }
  } catch (e) {
    console.error(e);
  }
  try {
    // look through other related content, for now to locate video
    const relatedVideo =
      typeMap?.[internal.ID_MANAGER_RELATED_CONTENT_GENERATOR];
    let video = [];
    if (relatedVideo) {
      relatedVideo.forEach((item) => {
        // hardcode the video logic for youtube
        if (item.id.toLowerCase().includes("youtu")) {
          item.type = "video/youtube";
          video.push({
            enableYouTube: true,
            sources: [{ ...item, src: item.id }]
          });
        }
      });
      mediaInfo.video = video;
    }

    ref.relatedMedia = mediaInfo;
  } catch (e) {
    console.error(e);
  }
}

/**
 * Adds the provenance owners to the store
 *
 * @param {object} lodObject - the HMO
 * @param {object} ref - the ref to the store
 * @async
 */
async function addProvenance(lodObject, ref) {
  let provenanceOwners = [];
  let ownerRefs = getOwners(lodObject);
  let unknownOwners = ownerRefs
    .filter((obj) => {
      return obj && !localHumanMadeObjectHelpers.isMakerKnown(obj);
    })
    .map((a) => a.id);

  try {
    provenanceOwners = await dataService.getAllById(
      ownerRefs.map((a) => a.id),
      dataUrl
    );
  } catch (e) {
    console.error(e);
  }

  // what happens with unknown owners

  ref.provenance = {
    data: getProvenanceData(lodObject, provenanceOwners, unknownOwners),
    notes: getOverallProvenanceNotes(lodObject)
  };
}

/**
 * Adds the bibliographic information to the store
 *
 * @param {object} lodObject - the HMO
 * @param {object} ref - the ref to the store
 * @async
 */
async function addBibliography(lodObject, ref) {
  const references =
    localHumanMadeObjectHelpers.getBibliographicTabData(lodObject);
  const about = references
    .map((ref) => ref.linguisticObjectId)
    .filter((r) => r != undefined);
  let bibs = [];

  try {
    bibs = await dataService.getAllById(about, dataUrl);
  } catch (e) {
    console.error(e);
  }

  ref.bibliography = localHumanMadeObjectHelpers
    .getBibliographicLinks(references, bibs, lodObject)
    .map((v) => {
      return removeEmpty({
        id: v.id,
        url: v.url,
        content: v.content
      });
    });
}

/**
 * Adds the exhibition information to the store
 *
 * @param {object} lodObject - the HMO JSON-LD Object
 * @param {object} ref - the reference to the store
 * @async
 */
async function addExhibitionInformation(lodObject, ref) {
  let exhibitionIds = localHumanMadeObjectHelpers.getExhibitionIDs(lodObject);
  let exhibitions = [];
  let allStubs = [];
  let motivations = [];
  let places = [];
  try {
    exhibitions = await dataService.getAllById(exhibitionIds, dataUrl);
    allStubs = getAllStubs(exhibitions);
  } catch (e) {
    console.error(e);
  }

  try {
    motivations = await dataService.getAllById(
      allStubs.motivations.map((a) => a.id),
      dataUrl
    );
  } catch (e) {
    console.error(e);
  }
  try {
    places = await dataService.getAllById(
      allStubs.places.map((a) => a.id),
      dataUrl
    );
  } catch (e) {
    console.error(e);
  }

  ref.exhibitions = getExhibitionAndLocations(
    getExhibitionArray({
      exhibitions: exhibitions,
      places: places,
      motivations: motivations
    })
  );
}

/**
 * Takes an array of objects that may need routes/slugs added to them,
 * filtering out any undefined/unknown and objects that don't need a link
 * as well as objects we have already requested the slug for earlier in the store
 *
 * @param {array} objects - an array of stub objects with an id
 * @param {object} slugs - the map of slugs
 */
async function addRoutesToObjects(objects, slugs) {
  objects = objects.filter((o) => o?.id != undefined && o.hideLink != true);

  if (objects.length != 0) {
    let fetchedSlugs = await dataService.getSlugs(
      objects,
      idManagerDataUrl,
      internal.ID_MANAGER_SOURCE_GENERATOR,
      internal.ID_MANAGER_TARGET_GENERATOR
    );
    Object.assign(slugs, fetchedSlugs);
    objects.forEach((stub) => applyRouteToStub(stub, slugs));
  }
}

/**
 * Applies a route to a stub
 *
 * @param {object} stub - the stub object with an id
 * @param {object} slugs - the map of slugs
 *
 */
export function applyRouteToStub(stub, slugs) {
  if (stub?.id == undefined) {
    return;
  }
  const id = getIndexedId(stub.id);
  const entry = slugs[id];
  if (entry?.slug) {
    stub.to = {
      name: entry.path,
      params: {
        slug: entry.slug
      },
      state: {
        slug: entry.slug,
        indexedId: id
      }
    };
  }
}
